import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, permissionAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});

  const fetchPermissions = async () => {
    try {
      const res = await permissionAPI.getMyPermissions();
      setPermissions(res.data || {});
    } catch (error) {
      setPermissions({});
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');

    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
        fetchPermissions();
      } catch (e) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { access, refresh, user } = response.data;

      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      localStorage.setItem('user_data', JSON.stringify(user));

      setUser(user);
      fetchPermissions();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    setUser(null);
    setPermissions({});
  };
// Add this function to your AuthContext
const updateUser = (userData) => {
  setUser(userData);
  localStorage.setItem('user_data', JSON.stringify(userData));
};

// Whether the current user has a given action's access to a module code,
// per the live Permissions matrix - superadmins implicitly have access to
// everything. This is the single source of truth every CRUD button/nav item
// should check, instead of a hardcoded role list, so toggling a permission
// on the Permissions screen takes effect everywhere immediately.
const hasModulePermission = (moduleCode, action) => {
  if (user?.is_superuser) return true;
  return Boolean(permissions[moduleCode]?.[`can_${action}`]);
};

const canReadModule = (moduleCode) => hasModulePermission(moduleCode, 'read');
const canCreateModule = (moduleCode) => hasModulePermission(moduleCode, 'create');
const canUpdateModule = (moduleCode) => hasModulePermission(moduleCode, 'update');
const canDeleteModule = (moduleCode) => hasModulePermission(moduleCode, 'delete');

// Add it to the context value
const value = {
  user,
  login,
  logout,
  loading,
  isAuthenticated: !!user,
  isSuperAdmin: user?.is_superuser || false,
  updateUser, // Add this line
  permissions,
  canReadModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
};

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};